import AGREGAR_ADJUDICACION_DIRECTA from '@/graphql/mutations/administracion/adjudicaciones/agregarAdjudicacionDirecta.gql';
import AGREGAR_ADJUDICACION_DIRECTA_OBRA from '@/graphql/mutations/administracion/adjudicaciones/agregarAdjudicacionDirectaObra.gql';
import REMOVER_ADJUDICACION_DIRECTA from '@/graphql/mutations/administracion/adjudicaciones/removerAdjudicacionDirectaObra.gql';
import ADJUDICACIONES_DIRECTAS from '@/graphql/querys/administracion/adjudicaciones/adjudicacionesDirectas.gql';
import CONTRATISTAS from '@/graphql/querys/administracion/catalogos/contratistas/contratistas.gql';

export default {
  apollo: {
    contratistas: {
      query: CONTRATISTAS,
    },
  },
  methods: {
    /**
     * Agregar una obra a una adjudicación directa existente
     * @param {Object} params - Parámetros
     * @param {String} params.idAdjudicacionDirecta - ID de la adjudicación
     * @param {String} params.idObra - ID de la obra a agregar
     * @param {Number} params.importe - Importe de la obra
     * @param {Number} params.porcentajeIva - Porcentaje de IVA (default 16)
     * @returns {Promise<Object|null>} - Obra agregada o null si falló
     */
    async mixAgregarAdjudicacionDirectaObra({
      idAdjudicacionDirecta,
      idObra,
      importe,
      porcentajeIva = 16,
    }) {
      const importeNum = Number(importe);
      const porcentajeIvaNum = Number(porcentajeIva);
      const iva = this.$numberRound(importeNum * (porcentajeIvaNum / 100));
      const total = this.$numberRound(importeNum + iva);

      try {
        const { data } = await this.$apollo.mutate({
          mutation: AGREGAR_ADJUDICACION_DIRECTA_OBRA,
          variables: {
            idAdjudicacionDirecta,
            idObra,
            importe: importeNum,
            porcentajeIva: porcentajeIvaNum,
            iva,
            total,
          },
          update: (store, { data: { agregarAdjudicacionDirectaObra } }) => {
            try {
              const cacheData = store.readQuery({ query: ADJUDICACIONES_DIRECTAS });
              const adjIndex = cacheData.adjudicacionesDirectas.findIndex(
                (adj) => adj.id === idAdjudicacionDirecta,
              );
              if (adjIndex !== -1) {
                cacheData.adjudicacionesDirectas[adjIndex].obras.push(
                  agregarAdjudicacionDirectaObra,
                );
                store.writeQuery({ query: ADJUDICACIONES_DIRECTAS, data: cacheData });
              }
            } catch (cacheErr) {
              this.mixMensajeError(cacheErr);
            }
          },
        });

        this.mixMensajeSuccess('Obra agregada a la adjudicación');
        return data.agregarAdjudicacionDirectaObra;
      } catch (err) {
        this.mixMensajeError(err);
        return null;
      }
    },
    /**
     * Remover una obra de una adjudicación directa
     * @param {Object} params - Parámetros
     * @param {String} params.obraId - ID de la relación obra-adjudicación a remover
     * @param {String} params.adjudicacionId - ID de la adjudicación (para actualizar cache)
     * @returns {Promise<Boolean>} - true si fue exitoso
     */
    async mixRemoverAdjudicacionDirectaObra({ obraId, adjudicacionId }) {
      try {
        await this.$apollo.mutate({
          mutation: REMOVER_ADJUDICACION_DIRECTA,
          variables: {
            id: obraId,
          },
          update: (store) => {
            try {
              const data = store.readQuery({ query: ADJUDICACIONES_DIRECTAS });
              const adjIndex = data.adjudicacionesDirectas.findIndex(
                (adj) => adj.id === adjudicacionId,
              );
              if (adjIndex !== -1) {
                const obraIndex = data.adjudicacionesDirectas[adjIndex].obras.findIndex(
                  (o) => o.id === obraId,
                );
                if (obraIndex !== -1) {
                  data.adjudicacionesDirectas[adjIndex].obras.splice(obraIndex, 1);
                  store.writeQuery({ query: ADJUDICACIONES_DIRECTAS, data });
                }
              }
            } catch (cacheErr) {
              this.mixMensajeError(cacheErr);
            }
          },
        });

        this.mixMensajeSuccess('Obra removida de la adjudicación');
        return true;
      } catch (err) {
        this.mixMensajeError(err);
        return false;
      }
    },
    async mixAgregarAdjudicacionDirecta() {
      const { porcentajeIva } = this.adjudicacionDirecta;
      this.enviandoFormulario = true;
      this.mixEsCargando();

      // Formato de las obras seleccionadas
      this.adjudicacionDirecta.obras = this.obrasSelected.map(({ id, importe }) => ({
        idObra: id,
        importe,
        porcentajeIva,
        iva: (importe * (porcentajeIva / 100)),
        total: (importe * ((porcentajeIva / 100 + 1))),
      }));

      try {
        await this.$apollo.mutate({
          mutation: AGREGAR_ADJUDICACION_DIRECTA,
          variables: {
            adjudicacionDirecta: this.adjudicacionDirecta,
          },
          update: (store, { data: { agregarAdjudicacionDirecta } }) => {
            const data = store.readQuery({ query: ADJUDICACIONES_DIRECTAS });
            data.adjudicacionesDirectas.push(agregarAdjudicacionDirecta);
            store.writeQuery({ query: ADJUDICACIONES_DIRECTAS, data });
          },
        });

        this.mixMensajeSuccess();
        // Refrescar el listado de obras disponibles en el picker
        if (this.$refs.obrasPicker) {
          this.$refs.obrasPicker.refrescarListado();
        }
      } catch (err) {
        this.mixMensajeError(err);
      } finally {
        this.enviandoFormulario = false;
        this.mixEsCargando();
        this.cerrar();
      }
    },
    async mixEliminarAdjudicacionDirecta() {
      this.enviandoFormulario = true;
      this.mixEsCargando();

      try {
        await this.$apollo.mutate({
          mutation: REMOVER_ADJUDICACION_DIRECTA,
          variables: {
            id: this.adjudicacionDirectaEliminar.id,
          },
          update: (store) => {
            const data = store.readQuery({ query: ADJUDICACIONES_DIRECTAS });
            const { adjudicacionesDirectas } = data;
            const index = adjudicacionesDirectas
              .findIndex((adjudicacionDirecta) => adjudicacionDirecta.id
              === this.adjudicacionDirectaEliminar.id);
            if (index !== -1) {
              adjudicacionesDirectas.splice(index, 1);
            }
            store.writeQuery({ query: ADJUDICACIONES_DIRECTAS, data });
          },
        });

        this.mixMensajeSuccess();
      } catch (err) {
        this.mixMensajeError(err);
      } finally {
        this.enviandoFormulario = false;
        this.mixEsCargando();
        this.cerrar();
      }
    },
  },
};
